import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LinkIcon, UserPlus, Wallet, Check, Plus, DollarSign, Ban, Trash2, Pencil, RotateCcw, Search, ImagePlus, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const AdminAffiliateOps = ({ client, API }) => {
  const [links, setLinks] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [newLink, setNewLink] = useState({ name: "", slug: "", product_url: "" });
  const [editUrl, setEditUrl] = useState({});
  const [assign, setAssign] = useState({ user_id: "", master_link_id: "" });
  const [conv, setConv] = useState({ user_id: "", amount: "" });
  const [payout, setPayout] = useState({ user_id: "", amount: "" });
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [logoVersion, setLogoVersion] = useState(0);
  const fileInputs = useRef({});

  const uploadLogo = async (linkId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await client().post(`${API}/admin/links/${linkId}/logo`, fd);
      toast.success("Logo uploaded");
      setLogoVersion((v) => v + 1);
      loadAll();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Upload failed");
    }
  };

  const filteredAmbassadors = ambassadors.filter((a) => {
    const q = search.trim().toLowerCase();
    const matchQ = !q || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q) || (a.handle || "").includes(q);
    const matchTier = tierFilter === "all" || a.tier === tierFilter;
    return matchQ && matchTier;
  });

  const loadAll = useCallback(() => {
    const c = client();
    c.get(`${API}/admin/links`).then((r) => setLinks(r.data.links));
    c.get(`${API}/admin/ambassadors`).then((r) => setAmbassadors(r.data.ambassadors));
    c.get(`${API}/admin/payouts`).then((r) => setPayouts(r.data.payouts));
  }, [client, API]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); loadAll(); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed"); }
  };

  const toggleSuspend = (a) => {
    if (!a.suspended && !window.confirm(`Suspend ${a.name}? They will be blocked from logging in.`)) return;
    act(() => client().put(`${API}/admin/users/${a.id}`, { suspended: !a.suspended }), a.suspended ? `${a.name} reactivated` : `${a.name} suspended`);
  };

  const deleteUser = (a) => {
    if (!window.confirm(`Permanently delete ${a.name} (${a.email})? This cannot be undone.`)) return;
    act(() => client().delete(`${API}/admin/users/${a.id}`), `${a.name} deleted`);
  };

  const editHandle = (a) => {
    const h = window.prompt(`New handle for ${a.name} (lowercase letters & numbers only):`, a.handle || "");
    if (!h || h === a.handle) return;
    if (!/^[a-z0-9]{2,24}$/.test(h)) { toast.error("Handle must be 2-24 lowercase letters/numbers"); return; }
    act(() => client().put(`${API}/admin/users/${a.id}`, { handle: h }), `Handle changed to /go/${h}`);
  };

  return (
    <div className="space-y-12">
      {/* Master links */}
      <section>
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><LinkIcon className="w-5 h-5 text-emerald-600" /> Master links</h3>
        <div className="space-y-3 mb-5">
          {links.map((l) => (
            <div key={l.id} data-testid={`admin-link-${l.slug}`} className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  {l.logo_path ? (
                    <div className="relative group shrink-0">
                      <img src={`${API}/link-logo/${l.id}?v=${logoVersion}`} alt={`${l.name} logo`} data-testid={`admin-link-logo-${l.slug}`}
                        className="w-10 h-10 rounded-lg object-cover border border-slate-200" />
                      <button onClick={() => act(() => client().delete(`${API}/admin/links/${l.id}/logo`), "Logo removed")}
                        data-testid={`admin-link-logo-remove-${l.slug}`} aria-label="Remove logo"
                        className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => fileInputs.current[l.id]?.click()} data-testid={`admin-link-logo-upload-${l.slug}`} title="Upload logo"
                      className="w-10 h-10 shrink-0 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:border-emerald-400 transition-colors duration-200">
                      <ImagePlus className="w-4 h-4" />
                    </button>
                  )}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                    ref={(el) => { fileInputs.current[l.id] = el; }}
                    onChange={(e) => { uploadLogo(l.id, e.target.files[0]); e.target.value = ""; }} />
                  <p className="text-sm font-semibold">{l.name} <span className="text-slate-400 font-normal">· /{l.slug}</span>
                    {l.is_default && <span className="ml-2 text-[10px] font-semibold uppercase text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">default</span>}
                  </p>
                </div>
                <button onClick={() => act(() => client().put(`${API}/admin/links/${l.id}`, { active: !l.active }), l.active ? "Link deactivated" : "Link activated")}
                  data-testid={`admin-link-toggle-${l.slug}`}
                  className={`text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${l.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500"}`}>
                  {l.active ? "Active" : "Inactive"}
                </button>
              </div>
              <div className="flex gap-2">
                <input defaultValue={l.product_url} onChange={(e) => setEditUrl({ ...editUrl, [l.id]: e.target.value })}
                  data-testid={`admin-link-url-${l.slug}`}
                  className="flex-1 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <button onClick={() => editUrl[l.id] && act(() => client().put(`${API}/admin/links/${l.id}`, { product_url: editUrl[l.id] }), "Master URL updated")}
                  data-testid={`admin-link-save-${l.slug}`}
                  className="px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-700 transition-colors duration-200"><Check className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input placeholder="Name" value={newLink.name} onChange={(e) => setNewLink({ ...newLink, name: e.target.value })} data-testid="admin-new-link-name" className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm" />
          <input placeholder="slug (a-z0-9-)" value={newLink.slug} onChange={(e) => setNewLink({ ...newLink, slug: e.target.value })} data-testid="admin-new-link-slug" className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm" />
          <input placeholder="https://product-url.com" value={newLink.product_url} onChange={(e) => setNewLink({ ...newLink, product_url: e.target.value })} data-testid="admin-new-link-url" className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm" />
          <button onClick={() => act(() => client().post(`${API}/admin/links`, newLink).then(() => setNewLink({ name: "", slug: "", product_url: "" })), "Link created")}
            data-testid="admin-new-link-create" className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors duration-200"><Plus className="w-4 h-4" /> Add link</button>
        </div>
      </section>

      {/* Ambassadors + assignment */}
      <section>
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" /> Ambassadors</h3>
        <div className="flex flex-col md:flex-row gap-3 mb-5 bg-slate-50 border border-dashed border-slate-300 rounded-lg p-5">
          <Select value={assign.user_id} onValueChange={(v) => setAssign({ ...assign, user_id: v })}>
            <SelectTrigger data-testid="assign-user-select" className="rounded-lg bg-white md:w-64"><SelectValue placeholder="Ambassador…" /></SelectTrigger>
            <SelectContent>{ambassadors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} (@{a.handle})</SelectItem>)}</SelectContent>
          </Select>
          <Select value={assign.master_link_id} onValueChange={(v) => setAssign({ ...assign, master_link_id: v })}>
            <SelectTrigger data-testid="assign-link-select" className="rounded-lg bg-white md:w-64"><SelectValue placeholder="Master link…" /></SelectTrigger>
            <SelectContent>{links.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={() => assign.user_id && assign.master_link_id && act(() => client().post(`${API}/admin/assignments`, assign), "Link assigned")}
            data-testid="assign-submit-button" className="px-6 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors duration-200">Assign link</button>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or handle…"
              data-testid="ambassador-search-input"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger data-testid="ambassador-tier-filter" className="rounded-lg bg-white sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tiers</SelectItem>
              <SelectItem value="Noobie">Noobie</SelectItem>
              <SelectItem value="Rising">Rising</SelectItem>
              <SelectItem value="Elite">Elite</SelectItem>
              <SelectItem value="Superstar">Superstar</SelectItem>
            </SelectContent>
          </Select>
          {(search || tierFilter !== "all") && (
            <p className="text-sm text-slate-500 self-center" data-testid="ambassador-filter-count">{filteredAmbassadors.length} of {ambassadors.length} shown</p>
          )}
        </div>
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200">
              <th className="p-4">Ambassador</th><th className="p-4">Handle</th><th className="p-4">Tier</th><th className="p-4">Clicks (mo)</th><th className="p-4">Earned</th><th className="p-4">Balance</th><th className="p-4">Links</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {filteredAmbassadors.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-400" data-testid="ambassador-no-results">No ambassadors match your search.</td></tr>
              )}
              {filteredAmbassadors.map((a) => (
                <tr key={a.id} data-testid={`ambassador-row-${a.handle}`} className={`border-b border-slate-100 ${a.suspended ? "opacity-60" : ""}`}>
                  <td className="p-4 font-medium">{a.name}<p className="text-xs text-slate-400 font-normal">{a.email}</p></td>
                  <td className="p-4 font-mono text-xs text-emerald-700">/go/{a.handle}</td>
                  <td className="p-4">{a.tier}</td>
                  <td className="p-4">{a.clicks_this_month}</td>
                  <td className="p-4">${a.earned.toFixed(2)}</td>
                  <td className="p-4 font-semibold">${a.balance.toFixed(2)}</td>
                  <td className="p-4 text-slate-500">{a.assigned_link_ids.length}</td>
                  <td className="p-4">
                    {a.suspended
                      ? <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-0.5 rounded-full">Suspended</span>
                      : <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">Active</span>}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => editHandle(a)} data-testid={`user-edit-handle-${a.handle}`} title="Edit handle"
                        className="p-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => toggleSuspend(a)} data-testid={`user-suspend-${a.handle}`} title={a.suspended ? "Reactivate" : "Suspend"}
                        className={`p-2 rounded-full transition-colors duration-200 ${a.suspended ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-500 hover:bg-amber-50 hover:text-amber-600"}`}>
                        {a.suspended ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </button>
                      <button onClick={() => deleteUser(a)} data-testid={`user-delete-${a.handle}`} title="Delete user"
                        className="p-2 rounded-full text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Conversions + payouts */}
      <section>
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" /> Conversions & payouts</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-5 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Record conversion (commission auto-set by tier)</p>
            <Select value={conv.user_id} onValueChange={(v) => setConv({ ...conv, user_id: v })}>
              <SelectTrigger data-testid="conversion-user-select" className="rounded-lg bg-white"><SelectValue placeholder="Ambassador…" /></SelectTrigger>
              <SelectContent>{ambassadors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
            <input type="number" min="1" placeholder="Sale amount ($)" value={conv.amount} onChange={(e) => setConv({ ...conv, amount: e.target.value })} data-testid="conversion-amount-input" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm" />
            <button onClick={() => conv.user_id && conv.amount && act(() => client().post(`${API}/admin/conversions`, { user_id: conv.user_id, master_link_id: links[0]?.id || "", amount: parseFloat(conv.amount) }).then(() => setConv({ user_id: "", amount: "" })), "Conversion recorded")}
              data-testid="conversion-submit-button" className="w-full py-2 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors duration-200">Record conversion</button>
          </div>
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-5 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Create payout (from balance)</p>
            <Select value={payout.user_id} onValueChange={(v) => setPayout({ ...payout, user_id: v })}>
              <SelectTrigger data-testid="payout-user-select" className="rounded-lg bg-white"><SelectValue placeholder="Ambassador…" /></SelectTrigger>
              <SelectContent>{ambassadors.filter((a) => a.balance > 0).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} — ${a.balance.toFixed(2)} available</SelectItem>)}</SelectContent>
            </Select>
            <input type="number" min="1" step="0.01" placeholder="Payout amount ($)" value={payout.amount} onChange={(e) => setPayout({ ...payout, amount: e.target.value })} data-testid="payout-amount-input" className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm" />
            <button onClick={() => payout.user_id && payout.amount && act(() => client().post(`${API}/admin/payouts`, { user_id: payout.user_id, amount: parseFloat(payout.amount) }).then(() => setPayout({ user_id: "", amount: "" })), "Payout created")}
              data-testid="payout-submit-button" className="w-full py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors duration-200">Create payout</button>
          </div>
        </div>
        <div className="space-y-2">
          {payouts.length === 0 && <p data-testid="payouts-empty" className="text-sm text-slate-400">No payouts yet.</p>}
          {payouts.map((p) => (
            <div key={p.id} data-testid={`payout-row-${p.id}`} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-5 py-3">
              <p className="text-sm font-medium">{p.ambassador_name} <span className="text-slate-400 font-normal">· ${p.amount.toFixed(2)} via {p.method}</span></p>
              {p.status === "pending" ? (
                <button onClick={() => act(() => client().put(`${API}/admin/payouts/${p.id}/mark-paid`), "Marked paid")}
                  data-testid={`payout-mark-paid-${p.id}`} className="px-4 py-1.5 rounded-full bg-amber-500 text-white text-xs font-medium hover:bg-amber-400 transition-colors duration-200">Mark paid</button>
              ) : (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">Paid</span>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
